/**
 * Google Tag Manager + dataLayer integration
 *
 * Centraliserad modul för GTM-initiering och dataLayer-events.
 * Container-ID finns i index.html. Semantiko CMP hanterar samtycke.
 *
 * Events som pushas:
 * - virtual_page_view: Vid SPA-navigering (route change)
 * - view_contact_form: När offertmodal/formulär visas
 * - lead_form_submit: Vid formulärinskick (offert, exit-intent, inline CTA)
 * - exit_intent_shown: När exit-intent popup visas
 *
 * Enhanced Conversions:
 * - user_data bifogas bara när befintlig CMP uttryckligen medger marketing.
 * - värdena rensas direkt efter händelsen och återspelas aldrig vid senare samtycke.
 */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    loadGTM?: () => void;
    __gtmLoaded?: boolean;
    __appHydrated?: boolean;
    __gtmWanted?: boolean;
    SemantikoCMP?: {
      getConsent: () => { marketing?: boolean; analytics?: boolean } | null;
    };
  }
}

// Ensure dataLayer exists
if (typeof window !== "undefined") {
  window.dataLayer = window.dataLayer || [];
}

function push(event: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  try {
    window.dataLayer.push(event);
  } catch {
    /* Tag errors must not interrupt forms. */
  }
}

function hasMarketingConsent(): boolean {
  try {
    return window.SemantikoCMP?.getConsent()?.marketing === true;
  } catch {
    return false;
  }
}

/**
 * GTM (GTM-MMS2MD4L) is injected from index.html after the first user
 * interaction (scroll, move, click, key) or a 4s timeout — so the CMP
 * does not become LCP. This function is a no-op kept for callers.
 */
export function initGTM() {
  // Intentionally a no-op. GTM is loaded from index.html after the first
  // user interaction (or 4s) so the CMP does not become LCP.
}

/**
 * Push virtual page view on SPA navigation.
 * Should be called from the router on every route change.
 */
export function trackPageView(path: string, title?: string) {
  if (typeof window === "undefined") return;
  push({
    user_data: null,
    event: "virtual_page_view",
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.origin + path,
  });
}

/**
 * Push event when contact/offert form becomes visible.
 * Useful for retargeting audiences who viewed the form.
 */
export function trackFormView(source: string) {
  if (typeof window === "undefined") return;
  push({
    user_data: null,
    event: "view_contact_form",
    form_source: source,
    page_path: window.location.pathname,
  });
}

/**
 * Push lead form submission event with Enhanced Conversions user data.
 * This event should be used as conversion trigger in GTM for:
 * - Google Ads Enhanced Conversions
 * - Meta Pixel (via GTM or server-side)
 */
export function trackLeadSubmit(data: {
  name: string;
  phone: string;
  email?: string;
  city?: string;
  source: string;
}) {
  if (typeof window === "undefined") return;
  // Enhanced Conversions user data
  // Google Ads uses this to match conversions to users
  // https://support.google.com/google-ads/answer/13258081
  const userData: Record<string, string> = {};
  const marketingAllowed = hasMarketingConsent();
  if (marketingAllowed && data.email)
    userData.email = data.email.trim().toLowerCase();
  if (marketingAllowed && data.phone) {
    // Normalize Swedish phone: 070... -> +4670...
    let phone = data.phone.replace(/[\s\-\(\)]/g, "");
    if (phone.startsWith("0") && !phone.startsWith("00")) {
      phone = "+46" + phone.slice(1);
    }
    userData.phone_number = phone;
  }
  if (marketingAllowed && data.name) {
    const parts = data.name.trim().split(/\s+/);
    if (parts.length >= 2) {
      userData.first_name = parts[0];
      userData.last_name = parts.slice(1).join(" ");
    } else {
      userData.first_name = parts[0];
    }
  }

  // Values must be present in the same push as the trigger event.
  // Keep enhanced-conversion data nested; never map it to GA4 event parameters.
  push({
    event: "lead_form_submit",
    form_source: data.source,
    page_path: window.location.pathname,
    user_data: marketingAllowed ? userData : null,
  });
  // GTM retains data model values between events. Clear them immediately so
  // unrelated tags and later consent changes cannot reuse this contact.
  push({ user_data: null });
}

/**
 * Push exit intent popup shown event.
 */
export function trackExitIntent() {
  if (typeof window === "undefined") return;
  push({
    user_data: null,
    event: "exit_intent_shown",
    page_path: window.location.pathname,
  });
}
